/**
 * FONTE ÚNICA (frontend) da identidade de planos: chaves, nomes e conversões.
 *
 * Antes `PLAN_NAME_TO_KEY` / `PLAN_KEY_TO_NAME` estavam DUPLICADOS em
 * `hooks/use-current-plan.ts`, `organisms/plans-management-tab` e `organisms/plans-catalog-tab`.
 *
 * A fonte de verdade dos LIMITES e do PLANO EFETIVO continua no backend
 * (`PLAN_LIMITS` + endpoint `GET signup/plans/limits/current`); o frontend consome via
 * `useCurrentPlan()`. Aqui ficam apenas as chaves/nomes e helpers de conversão.
 */

export type PlanKey =
  | "gamefik_free"
  | "teacher_epic"
  | "teacher_shiny"
  | "school_starter"
  | "school_pro"
  | "school_enterprise";

/** Nome comercial (como vem do Stripe `productName`) → chave interna do plano. */
export const PLAN_NAME_TO_KEY: Record<string, PlanKey> = {
  "Gamefik Free": "gamefik_free",
  "Teacher Epic": "teacher_epic",
  "Teacher Shiny": "teacher_shiny",
  "School Starter": "school_starter",
  "School Pro": "school_pro",
  "School Enterprise": "school_enterprise",
  Enterprise: "school_enterprise",
};

/** Chave interna do plano → nome comercial canônico. */
export const PLAN_KEY_TO_NAME: Record<string, string> = {
  gamefik_free: "Gamefik Free",
  teacher_epic: "Teacher Epic",
  teacher_shiny: "Teacher Shiny",
  school_starter: "School Starter",
  school_pro: "School Pro",
  school_enterprise: "School Enterprise",
};

/** Resolve a chave do plano a partir do nome comercial do produto (Stripe). */
export function resolvePlanKeyFromProductName(productName?: string | null): PlanKey | null {
  if (!productName) return null;
  return PLAN_NAME_TO_KEY[productName] ?? null;
}

/**
 * Ordem de tiers dos planos (quanto maior, "melhor"). Espelha `BASE_PLAN_RANK` do
 * backend (`plan-entitlement`). Planos desconhecidos ficam no piso (0); ausencia em -1.
 */
export const PLAN_RANK: Record<PlanKey, number> = {
  gamefik_free: 0,
  teacher_epic: 1,
  teacher_shiny: 2,
  school_starter: 3,
  school_pro: 4,
  school_enterprise: 5,
};

/** Rank de um plano; desconhecido no piso (0), ausencia em -1. */
export function planRank(planKey?: string | null): number {
  if (!planKey) return -1;
  return PLAN_RANK[planKey as PlanKey] ?? 0;
}

/** Forma minima de assinatura para resolucao de plano (compat. com SubscriptionPlan). */
type SubscriptionLike = {
  status?: string;
  productName?: string | null;
  addonType?: string | null;
  items?: Array<{
    id?: string | null;
    itemKind?: "subscription_item" | "invoice_line" | null;
    productName?: string | null;
    addonType?: string | null;
  }> | null;
};

function isPlanSubscriptionItem(item: NonNullable<SubscriptionLike["items"]>[number]): boolean {
  if (item.itemKind === "invoice_line") return false;
  const id = String(item.id || "");
  return !/^invoiceitem_/i.test(id) && !/^price_.*_line_/i.test(id);
}

/**
 * Plano-base de UMA assinatura: varre TODOS os itens (ignorando add-ons) e retorna o
 * plano-base de maior tier presente, ou null. Uma assinatura puramente add-on
 * (addonType no nivel da sub) tambem retorna null.
 */
export function resolveBasePlanKeyFromSubscription(sub: SubscriptionLike | null | undefined): PlanKey | null {
  if (!sub || sub.addonType) return null;
  let best: PlanKey | null = null;
  let bestRank = -1;
  const consider = (name?: string | null) => {
    const key = resolvePlanKeyFromProductName(name);
    if (!key) return;
    const rank = planRank(key);
    if (rank > bestRank) {
      best = key;
      bestRank = rank;
    }
  };
  const items = sub.items ?? [];
  if (items.length) {
    for (const item of items) {
      if (!isPlanSubscriptionItem(item)) continue;
      if (item?.addonType) continue;
      consider(item?.productName);
    }
  } else {
    consider(sub.productName);
  }
  return best;
}

/**
 * REGRA CANONICA (AGENTS.md secao 16): entre TODAS as assinaturas, retorna a que
 * carrega o plano-base de MAIOR tier (com o planKey resolvido) — nunca `subs[0]`. Um
 * customer pode ter varias assinaturas, e uma assinatura pode agrupar plano-base +
 * add-ons (IA, pacote de alunos). Responde "algum plano ativo e X?", nao "o primeiro e X".
 */
export function pickMainBaseSubscription<T extends SubscriptionLike>(
  subs: T[] | null | undefined,
  allowedStatuses: ReadonlySet<string> = new Set(["active", "trialing"])
): { subscription: T; planKey: PlanKey } | null {
  let best: { subscription: T; planKey: PlanKey } | null = null;
  let bestRank = -1;
  for (const sub of subs ?? []) {
    if (!allowedStatuses.has(String(sub.status || "").toLowerCase())) continue;
    const planKey = resolveBasePlanKeyFromSubscription(sub);
    if (!planKey) continue;
    const rank = planRank(planKey);
    if (rank > bestRank) {
      best = { subscription: sub, planKey };
      bestRank = rank;
    }
  }
  return best;
}
